import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

interface PrimaryTag {
  id: string;
  name: string;
  slug: string;
  sort_order: number;
}

interface PrimaryTagsSelectorProps {
  selectedTagIds: string[];
  onChange: (tagIds: string[]) => void;
  maxSelection?: number;
}

export default function PrimaryTagsSelector({ selectedTagIds, onChange, maxSelection = 2 }: PrimaryTagsSelectorProps) {
  const [tags, setTags] = useState<PrimaryTag[]>([]);

  useEffect(() => {
    loadTags();
  }, []);

  async function loadTags() {
    try {
      const { data } = await supabase
        .from('primary_tags')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (data) {
        setTags(data);
      }
    } catch (err) {
      console.error('Failed to load primary tags:', err);
    }
  }

  function handleToggle(tagId: string) {
    const isSelected = selectedTagIds.includes(tagId);

    if (isSelected) {
      onChange(selectedTagIds.filter(id => id !== tagId));
    } else {
      if (selectedTagIds.length >= maxSelection) {
        return;
      }
      onChange([...selectedTagIds, tagId]);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <label className="block text-[13px] font-medium text-[#A0A0A0]">
            Primary Tags (max {maxSelection})
          </label>
          {selectedTagIds.length === maxSelection && (
            <span className="text-[11px] text-[#FF6B6B]">
              Maximum of {maxSelection} primary tags selected. Remove one to choose another.
            </span>
          )}
        </div>
        <span className={`text-[13px] font-medium ${
          selectedTagIds.length === maxSelection
            ? 'text-[#40E0D0]'
            : 'text-[#606060]'
        }`}>
          {selectedTagIds.length} / {maxSelection} selected
        </span>
      </div>

      {/* Tag Grid - 2 columns on desktop, 1 on mobile */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {tags.map((tag) => {
          const isSelected = selectedTagIds.includes(tag.id);
          const isDisabled = !isSelected && selectedTagIds.length >= maxSelection;

          return (
            <label
              key={tag.id}
              className={`flex items-center p-4 rounded-lg border cursor-pointer transition-all ${
                isSelected
                  ? 'bg-[#40E0D0]/10 border-[#40E0D0]/40 shadow-sm shadow-[#40E0D0]/10'
                  : isDisabled
                  ? 'bg-[#0A0A0A] border-[#282828] opacity-40 cursor-not-allowed'
                  : 'bg-[#0A0A0A] border-[#282828] hover:border-[#383838] hover:bg-[#141414]'
              }`}
              title={isDisabled ? `Maximum ${maxSelection} tags allowed` : ''}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleToggle(tag.id)}
                disabled={isDisabled}
                className="sr-only"
              />
              <div className="flex-1 min-w-0 text-center">
                <div className="text-[14px] font-medium text-[#F5F5F5]">
                  {tag.name}
                </div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}
